import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSportConfig } from '../context/SportConfig';
import '../styles/SnakeDraft.css';

const POLL_INTERVAL = 7000; // 7 seconds — fast enough to feel live without hammering the server

function formatTimeRemaining(ms) {
  if (ms <= 0) return 'Expired';
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function SnakeDraft() {
  const { id } = useParams();
  const { token, user } = useAuth();
  const sportConfig = useSportConfig();
  const navigate = useNavigate();
  const navTitle = sportConfig ? `${sportConfig.appEmoji} ${sportConfig.appName}` : '…';

  const [league, setLeague] = useState(null);
  const [state, setState] = useState(null);      // DraftState from server
  const [picks, setPicks] = useState([]);
  const [available, setAvailable] = useState([]);
  const [myBudget, setMyBudget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pickError, setPickError] = useState(null);
  const [pickLoading, setPickLoading] = useState(false);
  const [myHeroPick, setMyHeroPick] = useState(null);
  const [tourFilter, setTourFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAllPlayers, setShowAllPlayers] = useState(false);
  const [timeDisplay, setTimeDisplay] = useState('');
  const [notStarted, setNotStarted] = useState(false);
  const [isCommissioner, setIsCommissioner] = useState(false);
  const [commError, setCommError] = useState(null);
  const [showStartModal, setShowStartModal] = useState(false);
  const [startSettings, setStartSettings] = useState(null);
  const [startLoading, setStartLoading] = useState(false);

  const pollRef = useRef(null);
  const tickRef = useRef(null);
  const deadlineRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL || '/api';

  // Fetch league info once
  const fetchLeague = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/leagues/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setLeague(data.league);
        setIsCommissioner(data.league.commissioner._id === user?._id
          || (data.league.coCommissioners || []).some(c => c._id === user?._id));
      }
    } catch {
      // ignore
    }
  }, [API_URL, id, token, user]);

  // Poll draft state
  const fetchState = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/snake/${id}/state`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.message === 'Draft has not been started') {
          setNotStarted(true);
        } else {
          setError(data.message);
        }
        return;
      }
      if (data.success) {
        setState(data.state);
        setPicks(data.picks);
        setAvailable(data.available);
        setMyBudget(data.myBudget);
        setMyHeroPick(data.myHeroPick || null);
        setNotStarted(false);
        // Store absolute deadline for local tick
        deadlineRef.current = data.state.paused
          ? null
          : Date.now() + data.state.timeRemaining;
      }
    } catch {
      // polling failure is non-fatal
    } finally {
      setLoading(false);
    }
  }, [API_URL, id, token]);

  // Initial load + periodic polling
  useEffect(() => {
    fetchLeague();
    fetchState();
    pollRef.current = setInterval(fetchState, POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, [fetchLeague, fetchState]);

  // Local clock tick — updates display every second without needing a server round-trip
  useEffect(() => {
    tickRef.current = setInterval(() => {
      if (!deadlineRef.current) {
        setTimeDisplay(state?.paused ? 'Paused' : '—');
        return;
      }
      const remaining = deadlineRef.current - Date.now();
      setTimeDisplay(formatTimeRemaining(remaining));
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [state]);

  const isMyTurn = state && user && state.currentDrafterId === user._id;
  const isHeroTurn = isMyTurn && state?.heroMode && !myHeroPick;

  // Compute who picks next based on current index and direction
  const nextDrafter = React.useMemo(() => {
    if (!state || !state.draftOrder || state.draftStatus === 'complete') return null;
    const n = state.draftOrder.length;
    const idx = state.currentTurnIndex;
    let nextIdx;
    if (state.direction === 'forward') {
      nextIdx = idx < n - 1 ? idx + 1 : idx; // turnaround: same person picks again
    } else {
      nextIdx = idx > 0 ? idx - 1 : idx; // turnaround: same person picks again
    }
    // nextIdx === idx means turnaround — same person picks next, already shown as "Now picking"
    if (nextIdx === idx || nextIdx < 0 || nextIdx >= n) return null;
    return state.draftOrder[nextIdx];
  }, [state]);

  const handlePick = async (playerId) => {
    setPickError(null);
    setPickLoading(true);
    try {
      const res = await fetch(`${API_URL}/snake/${id}/pick`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ playerId }),
      });
      const data = await res.json();
      if (data.success) {
        await fetchState(); // keep buttons disabled until new state lands
      } else {
        setPickError(data.message);
      }
    } catch {
      setPickError('Failed to submit pick');
    } finally {
      setPickLoading(false);
    }
  };

  // Self-skip: active drafter passes their own turn — no catch-up pick is owed
  const handleSelfSkip = async () => {
    if (!window.confirm('Skip your turn? You will lose this pick — there is no catch-up.')) return;
    setPickError(null);
    setPickLoading(true);
    try {
      const res = await fetch(`${API_URL}/snake/${id}/self-skip`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        fetchState();
      } else {
        setPickError(data.message);
      }
    } catch {
      setPickError('Failed to skip turn');
    } finally {
      setPickLoading(false);
    }
  };

  // Commissioner actions
  const commissionerAction = async (action) => {
    setCommError(null);
    try {
      const res = await fetch(`${API_URL}/snake/${id}/${action}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        fetchState();
      } else {
        setCommError(data.message);
      }
    } catch {
      setCommError(`Failed to ${action} draft`);
    }
  };

  const handleStartDraft = () => {
    setCommError(null);
    setStartSettings({
      snakePickTime: league?.snakePickTime || 5 * 60 * 60 * 1000,
      heroMode: league?.heroMode || false,
      heroPoolSize: league?.heroPoolSize || 5,
      draftBudget: league?.draftBudget || 175,
    });
    setShowStartModal(true);
  };

  const handleConfirmStart = async () => {
    setCommError(null);
    setStartLoading(true);
    try {
      const putRes = await fetch(`${API_URL}/leagues/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(startSettings),
      });
      const putData = await putRes.json();
      if (!putData.success) {
        setCommError(putData.message);
        setStartLoading(false);
        return;
      }
      const startRes = await fetch(`${API_URL}/snake/${id}/start`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const startData = await startRes.json();
      if (startData.success) {
        setShowStartModal(false);
        fetchState();
      } else {
        setCommError(startData.message);
      }
    } catch {
      setCommError('Failed to start draft');
    } finally {
      setStartLoading(false);
    }
  };

  const handleForceComplete = async () => {
    if (!window.confirm('Force-end the draft? This cannot be undone — all remaining picks will be cancelled.')) return;
    setCommError(null);
    try {
      const res = await fetch(`${API_URL}/snake/${id}/force-complete`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        fetchState();
      } else {
        setCommError(data.message);
      }
    } catch {
      setCommError('Failed to force-end draft');
    }
  };

  // Filtered player list — hide hero-pool entries once this manager has claimed their hero
  const filteredPlayers = available.filter(p => {
    if (p.isHeroOnly && myHeroPick) return false;
    if (!showAllPlayers && myBudget && p.costCredits > myBudget.remaining) return false;
    const matchesTour = tourFilter === 'ALL' || p.tour === tourFilter;
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesTour && matchesSearch;
  });

  // Build a map of pick number → manager name for the pick log
  const currentDrafter = state?.draftOrder?.[state?.currentTurnIndex];

  if (loading) return (
    <div className="snake-container">
      <p className="status-msg">Loading draft...</p>
    </div>
  );

  if (error) return (
    <div className="snake-container">
      <p className="status-msg error">{error}</p>
      <Link to={`/leagues/${id}`} className="btn btn-secondary">Back to League</Link>
    </div>
  );

  const draftComplete = state?.draftStatus === 'complete' || league?.draftStatus === 'complete';

  return (
    <div className="snake-container">
      <nav className="dashboard-nav">
        <div className="nav-left">
          <h1 className="nav-title">{navTitle}</h1>
        </div>
        <div className="nav-right">
          <Link to={`/leagues/${id}`} className="btn btn-secondary">League Detail</Link>
          <Link to="/dashboard" className="btn btn-secondary">Dashboard</Link>
        </div>
      </nav>

      <div className="snake-content">
        <header className="snake-header">
          <h2>🐍 Snake Draft — {league?.name}</h2>
          <div className="snake-header-meta">
            <span>💰 Budget: {myBudget?.total || league?.draftBudget} cr total</span>
            <span className={myBudget?.remaining < 20 ? 'budget-low' : ''}>
              🏦 Remaining: <strong>{myBudget?.remaining ?? '—'}</strong> cr
            </span>
            <span>📦 My picks: {myBudget ? (myBudget.total - myBudget.remaining) / 1 && picks.filter(p => p.manager._id === user?._id).length : 0}</span>
            {state && <span>⏱ {formatTimeRemaining(state.timePerPick)} per pick</span>}
          </div>
        </header>

        {/* Not started — commissioner can start, others wait */}
        {notStarted && (
          <div className="snake-not-started">
            {isCommissioner ? (
              <>
                <p>The snake draft hasn't started yet. When you're ready, start it to generate a random draft order.</p>
                <p className="draft-open-warning">⚠️ Once you start the draft, this league cannot be deleted.</p>
                <button className="btn btn-primary" onClick={handleStartDraft}>▶️ Start Snake Draft</button>
                {commError && <p className="form-error">{commError}</p>}
              </>
            ) : (
              <p>⏳ Waiting for the commissioner to start the snake draft...</p>
            )}
          </div>
        )}

        {/* Draft complete */}
        {draftComplete && (
          <div className="snake-complete-banner">
            🏁 Draft Complete! All picks are in.
          </div>
        )}

        {state && !notStarted && (
          <div className="snake-layout">

            {/* LEFT: Turn Status + Commissioner Controls */}
            <aside className="snake-sidebar">

              {/* Current turn */}
              <div className={`turn-card ${isMyTurn ? 'turn-card--yours' : ''}`}>
                {draftComplete ? (
                  <p className="turn-label">Draft finished</p>
                ) : (
                  <>
                    <p className="turn-label">
                      {isMyTurn ? (isHeroTurn ? "⚡ Pick your Hero!" : "⭐ It's your turn!") : 'Now drafting:'}
                    </p>
                    <p className="turn-drafter">
                      {currentDrafter?.firstName || currentDrafter?.username || '…'}
                    </p>
                    <p className="turn-meta">Round {state.round} · Pick {picks.length + 1}</p>
                    <div className={`turn-clock ${timeDisplay === 'Expired' ? 'turn-clock--expired' : ''}`}>
                      ⏱ {state.paused ? 'Paused' : timeDisplay}
                    </div>
                    {nextDrafter && (
                      <p className="turn-next">
                        Up next: <span>{nextDrafter.firstName || nextDrafter.username}</span>
                      </p>
                    )}
                    {isMyTurn && !state?.paused && (
                      <button
                        className="btn btn-secondary-outline skip-self-btn"
                        disabled={pickLoading}
                        onClick={handleSelfSkip}
                      >
                        ⏭ Skip My Turn
                      </button>
                    )}
                  </>
                )}
              </div>

              {/* Draft order */}
              <div className="draft-order-card">
                <h4>Draft Order</h4>
                <ol className="draft-order-list">
                  {state.draftOrder.map((m, idx) => {
                    const isCurrent = idx === state.currentTurnIndex && !draftComplete;
                    const isNext = !draftComplete && nextDrafter && m._id === nextDrafter._id;
                    return (
                      <li key={m._id} className={`draft-order-item ${isCurrent ? 'draft-order-item--active' : ''} ${isNext ? 'draft-order-item--next' : ''}`}>
                        <span className="order-name">{m.firstName || m.username}</span>
                        {isCurrent && <span className="picking-badge">picking</span>}
                        {isNext && !isCurrent && <span className="next-badge">next</span>}
                      </li>
                    );
                  })}
                </ol>
                <p className="snake-direction">
                  Direction: {state.direction === 'forward' ? '→ Forward' : '← Reverse'}
                </p>
              </div>

              {/* Commissioner controls */}
              {isCommissioner && !draftComplete && (
                <div className="comm-controls-card">
                  <h4>Commissioner Controls</h4>
                  {state.paused ? (
                    <button className="btn btn-primary" onClick={() => commissionerAction('resume')}>
                      ▶️ Resume Draft
                    </button>
                  ) : (
                    <button className="btn btn-accent" onClick={() => commissionerAction('pause')}>
                      ⏸ Pause Draft
                    </button>
                  )}
                  <button
                    className="btn btn-secondary-outline"
                    onClick={() => {
                      if (window.confirm('Skip the current manager? They will lose this pick — there is no catch-up.')) {
                        commissionerAction('skip');
                      }
                    }}
                  >
                    ⏭ Skip Current Manager
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={handleForceComplete}
                  >
                    ⏹ Force End Draft
                  </button>
                  {commError && <p className="form-error">{commError}</p>}
                </div>
              )}

              {/* My roster */}
              <div className="my-roster-card">
                <h4>My Roster ({picks.filter(p => p.manager._id === user?._id).length + (myHeroPick ? 1 : 0)} players)</h4>
                {!myHeroPick && !picks.filter(p => p.manager._id === user?._id).length ? (
                  <p className="empty-roster">No picks yet</p>
                ) : (
                  <ul className="my-roster-list">
                    {myHeroPick && (
                      <li className="my-roster-item my-roster-item--hero">
                        <span className="hero-badge-inline">⚡</span>
                        <span className="roster-name">{myHeroPick.player?.name}</span>
                        <span className="hero-free-badge">HERO</span>
                      </li>
                    )}
                    {picks
                      .filter(p => p.manager._id === user?._id)
                      .map(p => (
                        <li key={p._id} className="my-roster-item">
                          <span className="roster-name">{p.player.name}</span>
                          <span className="roster-cost">{p.costCredits} cr</span>
                        </li>
                      ))}
                  </ul>
                )}
              </div>
            </aside>

            {/* RIGHT: Player pool (only shown when active or browsing) */}
            <main className="snake-main">
              {/* Pick error */}
              {pickError && <p className="form-error pick-error">{pickError}</p>}

              {!isMyTurn && !draftComplete && (
                <div className="waiting-banner">
                  ⏳ Waiting for {currentDrafter?.firstName || currentDrafter?.username} to pick…
                </div>
              )}

              {/* Hero pick phase — shown when it's your turn and you haven't picked a hero */}
              {isHeroTurn ? (
                <div className="hero-phase-section">
                  <h3 className="hero-phase-title">⚡ Pick Your Hero</h3>
                  <p className="hero-phase-subtitle">Your hero is free, scores normally, and can't be changed. Regular picks start after.</p>
                  <div className="hero-pool-grid">
                    {(state.heroPool || []).map(p => (
                      <div key={p._id} className="hero-card">
                        <span className={`tour-badge tour-badge--${p.tour.toLowerCase()}`}>{p.tour}</span>
                        <span className="hero-card-name">{p.name}</span>
                        <span className="hero-card-rank">Rank #{p.ranking}</span>
                        <span className="hero-card-count">{p.heroPickCount} {p.heroPickCount === 1 ? 'manager' : 'managers'} picked this</span>
                        <button
                          className="btn btn-pick btn-hero-pick"
                          disabled={pickLoading}
                          onClick={() => handlePick(p._id)}
                        >
                          {pickLoading ? '…' : '⚡ Pick as Hero'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {/* Filters */}
                  <div className="player-filters">
                <div className="tour-filter-buttons">
                  {['ALL', 'ATP', 'WTA'].map(t => (
                    <button
                      key={t}
                      className={`filter-btn ${tourFilter === t ? 'filter-btn--active' : ''}`}
                      onClick={() => setTourFilter(t)}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                <input
                  className="player-search"
                  type="text"
                  placeholder="Search player..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
                <span className="available-count">{filteredPlayers.length} available</span>
                <button
                  className={`filter-btn ${showAllPlayers ? 'filter-btn--active' : ''}`}
                  onClick={() => setShowAllPlayers(v => !v)}
                >
                  {showAllPlayers ? 'Hide unaffordable' : 'Show all players'}
                </button>
              </div>

              {/* Player table */}
              <div className="player-pool-table-wrap">
                <table className="player-pool-table">
                  <thead>
                    <tr>
                      <th>Player</th>
                      <th>Tour</th>
                      <th>Rank</th>
                      <th>Cost</th>
                      {isMyTurn && !draftComplete && <th></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPlayers.map(p => {
                      const canAfford = myBudget && p.costCredits <= myBudget.remaining;
                      const heroOnly = !!p.isHeroOnly;
                      return (
                        <tr key={p._id} className={heroOnly ? 'player-row--hero-only' : (!canAfford ? 'player-row--unaffordable' : '')}>
                          <td className="player-name-cell">
                            {p.name}
                            {heroOnly && <span className="hero-only-badge"> ⚡ HERO</span>}
                          </td>
                          <td><span className={`tour-badge tour-badge--${p.tour.toLowerCase()}`}>{p.tour}</span></td>
                          <td>{p.ranking}</td>
                          <td>{heroOnly ? <span className="cost-badge cost-badge--free">HERO</span> : <span className="cost-badge">{p.costCredits} cr</span>}</td>
                          {isMyTurn && !draftComplete && (
                            <td>
                              {!heroOnly && (
                                <button
                                  className="btn btn-pick"
                                  disabled={!canAfford || pickLoading}
                                  onClick={() => handlePick(p._id)}
                                >
                                  {pickLoading ? '…' : 'Pick'}
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                    {filteredPlayers.length === 0 && (
                      <tr>
                        <td colSpan={isMyTurn ? 5 : 4} className="empty-pool">No players match your filters</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pick log */}
              <div className="pick-log">
                <h4>Pick Log</h4>
                {picks.length === 0 ? (
                  <p className="empty-pool">No picks yet</p>
                ) : (
                  <ol className="pick-log-list" reversed>
                    {[...picks].reverse().map(p => (
                      <li key={p._id} className="pick-log-item">
                        <span className="pick-num">#{p.pickNumber}</span>
                        <span className="pick-manager">{p.manager.firstName || p.manager.username}</span>
                        <span className="pick-player">{p.player.name}</span>
                        <span className={`tour-badge tour-badge--${p.player.tour.toLowerCase()}`}>{p.player.tour}</span>
                        <span className="pick-cost">{p.costCredits} cr</span>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
                </>
              )}
            </main>
          </div>
        )}
      </div>

      {/* Start draft confirmation modal */}
      {showStartModal && startSettings && (
        <div className="start-modal-overlay" onClick={() => setShowStartModal(false)}>
          <div className="start-modal" onClick={e => e.stopPropagation()}>
            <h3>🐍 Confirm Draft Settings</h3>
            <p className="start-modal-sub">Review and adjust before locking in the draft order.</p>

            <label className="start-modal-field">
              <span>⏱ Time per pick</span>
              <select
                value={startSettings.snakePickTime}
                onChange={e => setStartSettings(s => ({ ...s, snakePickTime: Number(e.target.value) }))}
              >
                <option value={30 * 60 * 1000}>30 minutes</option>
                <option value={60 * 60 * 1000}>1 hour</option>
                <option value={2 * 60 * 60 * 1000}>2 hours</option>
                <option value={5 * 60 * 60 * 1000}>5 hours</option>
                <option value={12 * 60 * 60 * 1000}>12 hours</option>
                <option value={24 * 60 * 60 * 1000}>24 hours</option>
                <option value={48 * 60 * 60 * 1000}>48 hours</option>
              </select>
            </label>

            <label className="start-modal-field">
              <span>💰 Draft budget per manager</span>
              <input
                type="number"
                min="1"
                value={startSettings.draftBudget}
                onChange={e => setStartSettings(s => ({ ...s, draftBudget: Number(e.target.value) }))}
              />
            </label>

            <label className="start-modal-field start-modal-toggle">
              <span>⚡ Hero Mode</span>
              <input
                type="checkbox"
                checked={startSettings.heroMode}
                onChange={e => setStartSettings(s => ({ ...s, heroMode: e.target.checked }))}
              />
              <span className="start-modal-toggle-label">{startSettings.heroMode ? 'On' : 'Off'}</span>
            </label>

            {startSettings.heroMode && (
              <label className="start-modal-field">
                <span>🏆 Hero pool size (per tour)</span>
                <select
                  value={startSettings.heroPoolSize}
                  onChange={e => setStartSettings(s => ({ ...s, heroPoolSize: Number(e.target.value) }))}
                >
                  <option value={3}>Top 3 per tour (6 heroes total)</option>
                  <option value={5}>Top 5 per tour (10 heroes total)</option>
                  <option value={10}>Top 10 per tour (20 heroes total)</option>
                </select>
              </label>
            )}

            {commError && <p className="form-error">{commError}</p>}

            <div className="start-modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowStartModal(false)} disabled={startLoading}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleConfirmStart} disabled={startLoading}>
                {startLoading ? 'Starting…' : '▶️ Confirm & Start Draft'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SnakeDraft;
